/**
 * Classification helpers for the Solicitation of Record panel.
 *
 * `ProjectFile` never carries a `sourceKind` — the Connect adapter drops it —
 * so membership and amendment status are inferred from the fields that do
 * arrive (fileCategory, documentType, classification.primaryType,
 * amendmentSequence). Kept pure so it can be unit-tested without rendering.
 */ /**
 * True when the classifier has not spoken about this file at all — no
 * fileCategory, no documentType (or an explicit 'unknown'), and no
 * classification result. A manual upload sits here until something
 * downstream (zou's sync off baltigo's output) says otherwise, and that
 * may never happen. An absent classifier result is not an absent document:
 * it still counts as the proposal's source material (B5-06.3).
 */ export function isUnclassifiedUpload(file) {
    return !file.fileCategory && (!file.documentType || file.documentType === 'unknown') && !file.classification;
}
export function isSolicitationPackageMember(file) {
    return file.fileCategory === 'source_document' || file.documentType === 'source' || file.documentType === 'amendment' || file.classification?.primaryType === 'BASE' || file.classification?.primaryType === 'AMENDMENT' || isUnclassifiedUpload(file);
}
export function isAmendment(file) {
    return (file.amendmentSequence ?? 0) >= 1 || file.documentType === 'amendment' || file.classification?.primaryType === 'AMENDMENT';
}
export function isUnclassified(file) {
    const source = file.classification?.confidenceSource;
    return !file.classification || source === 'filename-estimate' || source === 'fallback';
}
export function kindLabel(file) {
    if (isAmendment(file)) return 'Amendment';
    if (isUnclassifiedUpload(file)) return 'Unclassified source';
    return 'Solicitation';
}
export function selectSolicitationPackage(files) {
    return files.filter(isSolicitationPackageMember);
}
function uploadedAtMs(file) {
    const ms = new Date(file.uploadedAt).getTime();
    return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}
/**
 * The amendment with the highest sequence (tie-break latest upload); falling
 * back to the latest-uploaded base document; undefined when the package is
 * empty.
 */ export function selectBasisDocument(files) {
    const pkg = selectSolicitationPackage(files);
    if (pkg.length === 0) return undefined;
    const amendments = pkg.filter(isAmendment);
    if (amendments.length > 0) {
        return [
            ...amendments
        ].sort((a, b)=>{
            const seqDiff = (b.amendmentSequence ?? 0) - (a.amendmentSequence ?? 0);
            if (seqDiff !== 0) return seqDiff;
            return uploadedAtMs(b) - uploadedAtMs(a);
        })[0];
    }
    const base = pkg.filter((f)=>!isAmendment(f));
    if (base.length === 0) return undefined;
    return [
        ...base
    ].sort((a, b)=>uploadedAtMs(b) - uploadedAtMs(a))[0];
}
/** Solicitation (base) members first, oldest to newest, then amendments in sequence order. */ export function selectOrderedPackage(files) {
    const pkg = selectSolicitationPackage(files);
    const base = pkg.filter((f)=>!isAmendment(f)).sort((a, b)=>uploadedAtMs(a) - uploadedAtMs(b));
    const amendments = pkg.filter(isAmendment).sort((a, b)=>{
        const seqDiff = (a.amendmentSequence ?? 0) - (b.amendmentSequence ?? 0);
        if (seqDiff !== 0) return seqDiff;
        return uploadedAtMs(a) - uploadedAtMs(b);
    });
    return [
        ...base,
        ...amendments
    ];
}
/** Package members uploaded strictly after the basis document. */ export function selectNewerUnreviewed(files, basis) {
    if (!basis) return [];
    const basisMs = uploadedAtMs(basis);
    return selectSolicitationPackage(files).filter((f)=>f.attachmentId !== basis.attachmentId && uploadedAtMs(f) > basisMs);
}
/**
 * Honest extraction status for the package, derived from processingStatus
 * and whether any insights actually exist. Undefined when there is no
 * package to report on.
 */ export function selectExtractionState(packageFiles, hasInsights) {
    if (packageFiles.length === 0) return undefined;
    const errorCount = packageFiles.filter((f)=>f.processingStatus === 'error').length;
    if (errorCount > 0) return {
        kind: 'error',
        count: errorCount
    };
    if (packageFiles.some((f)=>f.processingStatus === 'processing' || f.processingStatus === 'queued')) {
        return {
            kind: 'processing'
        };
    }
    if (packageFiles.every((f)=>f.processingStatus === 'none')) {
        return {
            kind: 'none-run'
        };
    }
    return hasInsights ? {
        kind: 'ok'
    } : {
        kind: 'empty'
    };
}
/** Whether the pipeline carries any grounded facts at all. */ export function hasAnyInsights(pipeline) {
    if (!pipeline) return false;
    return Object.values(pipeline).some((v)=>Array.isArray(v) && v.length > 0);
}
/** A human date, or undefined for a missing/garbage timestamp — never "Invalid Date". */ export function formatDate(iso) {
    if (!iso) return undefined;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return undefined;
    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}
